import React, { useState, useEffect, useContext, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link } from "react-router-dom";
import { Row, Col, Form, Spinner, Alert, InputGroup, Button } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function MyAccountPage() {
    const { t } = useContext(TranslatorContext);

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    // Eye toggle state
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Profile Form State
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        password_confirmation: ""
    });

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

    // 1. Fetch User Profile Data
    const fetchUserProfile = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${API_URL}/profile`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data) {
                setFormData((prev) => ({
                    ...prev,
                    name: response.data.name || "",
                    email: response.data.email || ""
                }));
            }
        } catch (err) {
            console.error("Error fetching profile:", err);
            setMessage({
                type: "danger",
                text: err.response?.data?.message || "Failed to load user profile."
            });
        } finally {
            setLoading(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // 2. Update User Profile Data
    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: "", text: "" });

        if (formData.password && formData.password !== formData.password_confirmation) {
            setMessage({ type: "danger", text: "Passwords do not match." });
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem("authToken");
            const payload = {
                name: formData.name,
                email: formData.email,
            };

            if (formData.password) {
                payload.password = formData.password;
            }

            const response = await axios.put(`${API_URL}/profile`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage({
                type: "success",
                text: response.data?.message || "Profile updated successfully!"
            });

            setFormData((prev) => ({
                ...prev,
                password: "",
                password_confirmation: ""
            }));
        } catch (err) {
            console.error("Error updating profile:", err);
            setMessage({
                type: "danger",
                text: err.response?.data?.message || "Failed to update profile."
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t("my_account")}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t("my_account")}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={8} className="mx-auto">
                    <div className="mc-card p-4">
                        <h5 className="mb-4">{t("Update Account Information")}</h5>

                        {message.text && (
                            <Alert variant={message.type} dismissible onClose={() => setMessage({ type: "", text: "" })}>
                                {message.text}
                            </Alert>
                        )}

                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted">Loading profile details...</p>
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter full name"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Email Address</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Enter email address"
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                New Password <small className="text-muted">(Optional)</small>
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    value={formData.password}
                                                    onChange={handleChange}
                                                    placeholder="Enter new password"
                                                    minLength={8}
                                                />
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className="material-icons text-muted fs-6 align-middle">
                                                        {showPassword ? "visibility_off" : "visibility"}
                                                    </i>
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Confirm Password</Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type={showConfirmPassword ? "text" : "password"}
                                                    name="password_confirmation"
                                                    value={formData.password_confirmation}
                                                    onChange={handleChange}
                                                    placeholder="Confirm new password"
                                                    minLength={8}
                                                />
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    tabIndex="-1"
                                                >
                                                    <i className="material-icons text-muted fs-6 align-middle">
                                                        {showConfirmPassword ? "visibility_off" : "visibility"}
                                                    </i>
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>

                                    <Col md={12} className="mt-4">
                                        <button
                                            type="submit"
                                            className="btn btn-primary px-4"
                                            disabled={submitting}
                                        >
                                            {submitting ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Saving...
                                                </>
                                            ) : (
                                                t("save_changes")
                                            )}
                                        </button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}